import type {
    AppliedHeuristicLogMessage,
    ConversionAlgoConfig,
    ConversionDal,
    IStageRunner,
    MobileAlgoContext,
    Predicate,
    ReadOnlyExtensionAPI,
    StageRuntimeStore,
    Stages
} from '../types'
import type {SnapshotDal} from '@wix/document-manager-core'
import {AlgorithmModes, SkipPredicate, StageName} from '../enums'
import _ from 'lodash'

const stageOrder: StageName[] = [
    StageName.COMPONENT_CATEGORIZATION,
    StageName.GROUPING,
    StageName.GROUPING_ORDER,
    StageName.INIT_CSS_MODELS,
    StageName.STRUCTURE_ORDER,
    StageName.HORIZONTAL_LAYOUT_SCALE,
    StageName.VERTICAL_LAYOUT_SCALE,
    StageName.TRANSFORM_STYLE,
    StageName.POST_PROCESS
]

const initSkipPredicates = (readOnlyExtensionAPI: ReadOnlyExtensionAPI, config: ConversionAlgoConfig) => ({
    [SkipPredicate.INVALID_FOR_GROUPING]: () =>
        config.mode === AlgorithmModes.Adaptive ||
        readOnlyExtensionAPI.components.isPinned(config.rootPointer) ||
        readOnlyExtensionAPI.components.isHeaderSection(config.rootPointer)
})

const initSubscriptions = () => ({
    [StageName.COMPONENT_CATEGORIZATION]: [],
    [StageName.GROUPING]: [SkipPredicate.INVALID_FOR_GROUPING],
    [StageName.GROUPING_ORDER]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.INIT_CSS_MODELS]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.STRUCTURE_ORDER]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.HORIZONTAL_LAYOUT_SCALE]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.VERTICAL_LAYOUT_SCALE]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.TRANSFORM_STYLE]: [SkipPredicate.SHOULD_RUN_ALGO],
    [StageName.POST_PROCESS]: [SkipPredicate.SHOULD_RUN_ALGO]
})

export class StageRunner implements IStageRunner {
    private predicates: Partial<Record<SkipPredicate, Predicate>> = {}
    private subscriptions: Record<StageName, SkipPredicate[]> = initSubscriptions()

    constructor(private stages: Stages, readOnlyExtensionAPI: ReadOnlyExtensionAPI, config: ConversionAlgoConfig) {
        this.predicates = initSkipPredicates(readOnlyExtensionAPI, config)
    }

    private async registerSkipPredicate(key: SkipPredicate, predicate: Predicate) {
        this.predicates[key] = predicate
    }

    private subscribeToSkipPredicate(stageName: StageName, key: SkipPredicate) {
        this.subscriptions[stageName].push(key)
    }

    private getRuntimeStore(stageName: StageName): StageRuntimeStore {
        return {
            registerSkipPredicate: this.registerSkipPredicate.bind(this),
            subscribeToSkipPredicate: _.partial(this.subscribeToSkipPredicate.bind(this), stageName)
        }
    }

    private flush() {
        this.predicates = {}
        this.subscriptions = initSubscriptions()
    }

    private shouldSkipStage(stageName: StageName): boolean {
        const skipPredicates = this.subscriptions[stageName]
        return skipPredicates.some(key => {
            try {
                return this.predicates[key]?.() ?? false
            } catch (error) {
                console.warn(`Skip predicate "${key}" threw an error:`, error)
                return false
            }
        })
    }

    async run(
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        pageId: string,
        config: ConversionAlgoConfig,
        logger?: AppliedHeuristicLogMessage[]
    ) {
        for (const stageName of stageOrder) {
            if (this.shouldSkipStage(stageName)) {
                continue
            }

            const stage = this.stages[stageName]
            await stage.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                config,
                this.getRuntimeStore(stageName),
                logger
            )
            conversionDal.commitChanges(stageName)
        }

        this.flush()
    }
}
